import Animation from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/brevity/animation';
import BaseTimeline from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/base-timeline';
import {
    checkHasClickEle,
    getAnimTargetKeyOrderMap,
    shouldDisableAnimCacheAndFilter,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-utils';
import { getTargetComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-component-relate';
import {
    ANIM_STATE,
    CHILD_TL,
    colorAnimTypeList,
} from '@src/js/js-keyframe-animation/style-converter/constants';
import { presetClassTypes } from '@src/slide/domain/ui-model/element-modeles/property/animation/constant';
import { checkClickElement } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-pointer-cursor';
import PlayerLayerService from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/layered-render/plalyer-state-layered/player-layer-service';
import AnimFiltersManager from '../anim-filters/anim-filters-manager';

// interface
import type AnimationModel from '@src/slide/domain/ui-model/element-modeles/anim-model';
import { CacheRendering, type InteractionEvent } from '@tencent/alloy-renderer';
import { IAnimStartParams, IAnimUpdateParams, IAnimList, IRecordData, ITimelineParams, IAnimCompleteParams, IAnimTarget } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-interface';
import { getToStylesOfOrder } from '@src/js/js-keyframe-animation/style-converter/convertAnimInfos';
import { DEFAULT_CANVAS_STYLE, LayerType, SAMPLE_INTERVAL, AllowLayer, AnimCanvasProperty, ATTR_LIST, COLOR } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-constants';
import TimeRecord from '@src/slide/infrastructure/module/independent/time-record';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { isDomGifPictureModel, isGroupModel } from '@src/slide/domain/ui-model/elements/element-judge';
import * as playerActions from '@player/src/actions/control';
import playerStore from '@player/src/store';
import { getMemory } from '@src/slide/infrastructure/module/independent/performance/memory';
import { IAnimSimpleStyle } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import isUndefined from 'lodash/isUndefined';
import { AnimType } from '@tencent/slide-sdk-model';
import { ENV } from '@src/slide/application/service/edit-extension/edit-extension-export';
import { IAttr } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/player-state-animation/anim-e2e-manager';

export default class SlideTimeline extends BaseTimeline {
    public realExecuteAnimationList;
    public layerService: PlayerLayerService;
    protected currentOrder: number;
    private targetAnimMap: Map<string, IAnimList> | null; // key => [aniModel1,animModel2]
    private animTargetStyleMap: Map<number, { [key: string]: IAnimSimpleStyle }> | null;
    private cacheMap: Map<number, Set<IAnimTarget>> = new Map();
    private killedAnimation: boolean;
    private animFPSMap: { [key: string]: Array<number> } = {};
    private animMemoryMap: { [key: string]: Array<number> } = {};
    private animPrevTimeMap: { [key: string]: number } = {};
    private animFrameCountMap: { [key: string]: number } = {};
    private fpsArr: Array<{ [key: string]: number | string }> = [];
    private memoryArr: Array<{ [key: string]: number | string }> = [];
    // animTargetKey => animModelKey => isAnimCompleted
    private animCompletedMap: Map<string, Map<string, boolean>> = new Map();

    public constructor({ animList, slideComponent, mediaManager,
        options, playManager }: ITimelineParams) {
        super({ animList, slideComponent, mediaManager, options, playManager });
        this.layerService = PlayerLayerService.getInstance();
        this.masterTimeline = this.getMasterTimeline(this.animList, slideComponent, this.layerService);
        this.realExecuteAnimationList = [];
        this.targetAnimMap = null;
        this.initAnimTargetStyleMap();
    }

    // 后翻
    public handleNextClick(order: number): void {
        this.killActiveAnimation();
        this.killedAnimation = false;
        if (!this.slideComponent) return;
        TimeRecord.start(`${this.slideComponent.options.slideModel.key}-${order}`);
        const startLabel = this.createAnimLabel({
            order,
            prefix: CHILD_TL,
            suffix: ANIM_STATE.START,
        });
        const endLabel = this.createAnimLabel({
            order,
            prefix: CHILD_TL,
            suffix: ANIM_STATE.COMPLETE,
        });
        this.currentOrder = order;
        const realExecuteAnimation = this.masterTimeline.tweenFromTo(startLabel, endLabel);
        this.realExecuteAnimationList.push(realExecuteAnimation);
    }

    // 前翻，注意是在同一页进行回退操作，而不是翻页操作
    public handlePrevClick(order: number): void {
        this.killActiveAnimation();
        const targetKeyList = this.getSameOrderTargetKeyList(order);
        this.currentOrder = order;
        targetKeyList.forEach((targetKey) => {
            const animTarget = getTargetComponent(targetKey, this.slideComponent);
            if (!animTarget) return;
            const model: IElementBaseModel = animTarget.factoryOptions?.model;
            const presetClass = model?.animList?.[0]?.props?.presetClass;
            const animType = model?.animList?.[0]?.props?.animType;
            if (isDomGifPictureModel(model)) {
                this.mediaManager.onInitAnimStyles(model, presetClass === presetClassTypes.ENTR);
            } else if (isGroupModel(model)) {
                model.getGifsInGroup().forEach(gifModel => this
                    .mediaManager.onInitAnimStyles(gifModel, presetClass === presetClassTypes.ENTR));
            }
            const keyBackStyle = this.getKeyBackStyle(order, targetKey);
            if (!keyBackStyle) return;
            this.lastStyleMap.set(targetKey, keyBackStyle);
            Animation.set(animTarget, keyBackStyle);
            AnimFiltersManager.getInstance().resetAnimFilters(
                animType,
                presetClass,
                animTarget,
            );

            this.handleColorAnimStyle(keyBackStyle, animTarget, animType);
        });
        // 此时可能不是masterTimeline的最后一个动画,不会触发masterTimeline的回调,autoRender并未开启,需手动render一次
        this.slideComponent?.options.getApplication?.()?.render();
    }

    /**
     * 设置动画目标元素的最后一帧样式
     * @returns void
     */
    public setFinishStyle(): void {
        const targetAnimMap = this.getTargetAnimMap(this.animList);
        if (!targetAnimMap.size) return;
        targetAnimMap.forEach((animModelList, targetKey) => {
            const animTarget = getTargetComponent(targetKey, this.slideComponent);
            if (!animTarget) return;
            // 向前翻页时，所有 gif 的状态都是动画已播放完成状态
            this.mediaManager.setPlayerGifAnimationFinishedState(animTarget.factoryOptions.model);
            const spidAnimFinishStyle = getToStylesOfOrder(animModelList, animModelList.length - 1);
            const animModel = animModelList[animModelList.length - 1];
            if (!isUndefined(spidAnimFinishStyle.rotate)) {
                (spidAnimFinishStyle.rotate as number) -= this.getElementRotate(animModel);
            }
            const formattedStyle = this.formatStyle(spidAnimFinishStyle) as IAnimSimpleStyle;
            const originalStyle = this.originalStyleMap.get(targetKey);
            if (!originalStyle) return;
            if (!animTarget.pivotX && !animTarget.pivotY) {
                // 这里的animTarget可能是经过动画变换过的，也可能是新的或者是销毁过重新创建的
                // 而每次动画都会更改pivot，故pivotX与pivotY均为0时可以认为这是新创建的，需要对pivot进行手动调整
                animTarget.pivotX = originalStyle.x;
                animTarget.x = originalStyle.x;
                animTarget.pivotY = originalStyle.y;
                animTarget.y = originalStyle.y;
            }
            if (animModel.props.presetClass === presetClassTypes.EXIT) {
                // 退出动画，向上切页时，元素需不渲染，以保证可点击元素不响应（超链接、音视频等）
                animTarget.visible = false;
            }
            const mergedStyle = this.getMergedStyle(formattedStyle, originalStyle);
            Animation.set(animTarget, mergedStyle);
            this.handleColorAnimStyle(mergedStyle, animTarget, animModel.props.animType as AnimType);
        });
    }

    /**
     * 清空该timeline下的所有子timeline以及所有子动画
     */
    public clearMasterTimeline(): void {
        this.masterTimeline.clear();
        this.realExecuteAnimationList = [];
        this.cacheMap = new Map();
        this.animFPSMap = {};
        this.animMemoryMap = {};
        this.animPrevTimeMap = {};
        this.animFrameCountMap = {};
        this.fpsArr = [];
        this.memoryArr = [];
        this.slideComponent = undefined;
        this.animCompletedMap = new Map();
    }

    /**
     * 中止并清除元素正在运行的动画，并将元素置于动画最后一帧的样式
     * 若传入了event对象，则阻止事件向上冒泡
     * @param event 事件对象
     * 注意：中断动画依旧会触发childTlComplete
     */
    public killActiveAnimation(event?: InteractionEvent): void {
        if (!this.realExecuteAnimationList.length) return;
        this.killedAnimation = true;
        for (let i = 0; i < this.realExecuteAnimationList.length; i++) {
            const animation = this.realExecuteAnimationList[i];
            if (!animation.isActive()) continue;
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }
            animation.progress(0.95).progress(1)
                .kill();
            this.realExecuteAnimationList.splice(i, 1);
        }
        this.currentOrder = -1;
    }

    /**
     * 该时间线的动画是否还在运行
     * @returns boolean
     */
    public hasActiveAnim = (): boolean => this.realExecuteAnimationList.some(anim => anim?.isActive());

    /**
     * 获取当前动画播放顺序
     * @returns 动画播放顺序
     */
    public getCurrentOrder(): number {
        return this.currentOrder;
    }

    /**
     * 在指定时刻暂定动画
     * @param duration 暂停时刻，单位s
     */
    public pause(duration: number): void {
        this.realExecuteAnimationList.forEach((animation) => {
            animation.pause(duration);
        });
    }

    /**
     * 重新开始动画
     */
    public restart(): void {
        this.realExecuteAnimationList.forEach((animation) => {
            animation.restart();
        });
    }

    protected handleMasterComplete(): void {
        this.resumeAutoRender();
        this.lastStyleMap = new Map();
        this.tdwAnimData();
    }

    protected handleAnimStart(params: IAnimStartParams): void {
        const { animTarget, fromStyle, toStyle, animModel } = params;
        const animModelKey = animModel?.key;
        const animTargetKey = animModel?.elementModel?.key;
        const animType = animModel?.props.animType;
        const order = animModel?.playOrder;
        let statusMap = this.animCompletedMap.get(animTargetKey);
        if (!statusMap) {
            statusMap = new Map();
            this.animCompletedMap.set(animTargetKey, statusMap);
        }
        statusMap.set(animModelKey, false);
        // 动画元素为非可点击元素时，canvas app开启停止更新事件热区渲染
        // 动画元素为可点击元素，app需要在动画过程中更新事件热区，比如动画中的触发器仍然可以触发动画
        // order为0的立即播放动画，需要更新事件热区，否则会导致该页面中的音视频无法点击
        this.setStopUpdateHitRegion(!!order && !checkClickElement(animTarget.factoryOptions));
        this.initTdwData(animModelKey, animType);

        super.handleAnimStart(params);

        this.setSlideClickMap();

        if (this.canBeSliced(animTargetKey, this.slideComponent?.options.slideModel)) {
            // 进行分层
            this.layerService.processSlice(params.animTarget.factoryOptions.model);
            this.layerService.addActiveAnimsKey(animTargetKey, LayerType.OPERATION);
        } else {
            // 无法分层，在最底层canvas进行动画，需标记
            this.layerService.addActiveAnimsKey(animTargetKey, LayerType.BASE);
        }


        this.updateCacheMap(fromStyle, toStyle, order, animTarget);
        // 使用动画缓存
        this.useAnimCache(animTarget, animType);
        this.tdwAnimReactTime(params.order);
        if (__IS_DEV__ || ENV.IS_TEST) {
            const startAttrObj = this.getCurrentStyle(animTarget) as IAttr;
            const animFilter = this.animFiltersManager.getAnimFilter(animTarget, animType);
            if (animFilter) {
                startAttrObj.filterAction = animFilter.action;
                startAttrObj.filterType = animFilter.type;
                startAttrObj.filterRate = animFilter.rate;
            }
            import('./anim-e2e-manager').then((module) => {
                const animE2EManager = module.default.getInstance();
                animE2EManager.setElementStartAttrMap(order, { [animTargetKey]: startAttrObj });
            });
        }
    }

    protected handleAnimComplete(params: IAnimCompleteParams): void {
        const { animModel, animTarget } = params;
        const animModelKey = animModel?.key;
        const animTargetKey = animModel?.elementModel?.key;
        const animType = animModel?.props.animType;
        const presetClass = animModel?.props?.presetClass;
        const statusMap = this.animCompletedMap.get(animTargetKey);
        const isAnimCompleted = statusMap?.get(animModelKey);
        if (isAnimCompleted) return;
        statusMap?.set(animModelKey, true);
        super.handleAnimComplete(params);
        // @ts-ignore
        animTarget?.cache?.clear();

        if (presetClass === presetClassTypes.EXIT) {
            animTarget.visible = false;
        }

        this.resetAnimPrevTime(animModelKey, animType);
        this.layerService.removeActiveAnimsKey(animTargetKey);
        this.layerService.removeOperationToBaseLayer(params.animTarget.factoryOptions.model);

        // 演示态下音视频正在播放，且其控制动画是**被打断**的情况下，音视频需要在**complete**停止播放
        // 动画被打断是指在某组动画没播完的情况下，播放了另外一组动画。
        if (this.animMediaControl?.isPlaying()) {
            this.completeAnimMediaControl(params);
        }
    }

    protected handleChildTlStart(animList: AnimationModel[][]): void {
        super.handleChildTlStart(animList);
        animList.forEach((animLst) => {
            animLst.forEach((animationModel) => {
                const { elementModel } = animationModel;
                elementModel && this.mediaManager.handlePlayerAnimationStart(elementModel as IElementBaseModel);
            });
        });
    }

    protected syncRender() {
        this.renderApplication();
    }

    /**
     * 多数情况下动画播放只能在动画元素层和操作层二者选其一
     * 无法进行分层时（例如触发器元素层级在动画元素之上），则在baseLayer进行动画播放
     * 当可以进行分层时，则在操作层operationLayer进行动画播放
     *
     * 特殊情况下：音视频gif这些元素的动画和层次在其上的元素动画同时播放时
     * 会出现baseLayer 和optLayer均有动画的情况
     */
    protected renderApplication() {
        if (this.layerService.hasActiveAnimInBaseLayer) {
            // baseLayer（最底层canvas）有动画在运行
            this.slideComponent?.options.getApplication?.()?.render();
        }

        if (this.layerService.hasActiveAnimInOptLayer) {
            // optLayer（中间层canvas）有动画在运行
            const application = this.layerService.operationLayer?.getApplication();
            if (application) {
                // 动画需要执行全局渲染，且动画没有使用requestRender（requestRender会自动调度全局/局部渲染），需要主动标记一下
                application.allRefresh = true;
                application.render();
            }
        }
    }

    /**
     * 获取当前动画运行所在层的application
     */
    protected getCurrentAnimApp() {
        if (this.layerService.hasActiveAnimInBaseLayer) {
            // 动画在baseLayer（最底层canvas）运行
            return this.slideComponent?.options.getApplication?.();
        }
        // 动画在操作层（中间层canvas）运行
        return this.layerService.operationLayer?.getApplication();
    }

    protected handleChildTlComplete(
        order: number,
        animList: AnimationModel[][],
        autoPlay?: boolean,
    ): void {
        // 一组动画全部播放完成后，将所有元素恢复到当前元素层渲染
        this.layerService.restoreToBaseLayer();
        // 存储动画元素的最后一帧样式，为动画回退做准备
        this.cacheBackStyle(order);

        super.handleChildTlComplete(order, animList);
        this.updateCache(order);

        animList.forEach((animLst) => {
            animLst.forEach((animationModel) => {
                const { elementModel } = animationModel;
                elementModel && this.mediaManager
                    .handlePlayerAnimationEnd(elementModel as IElementBaseModel, animationModel);
            });
        });
        if (autoPlay && !this.killedAnimation) {
            // 切换动画设置自动切页时，元素动画执行结束后自动播放下一个主队列动画
            playerStore.dispatch(playerActions.nextPage());
        }
    }

    protected handleMasterStart(): void {
        super.handleMasterStart();
        this.layerService.baseLayer = this.slideComponent?.elementsLayer;
    }

    /**
     * 对动画终态scale>1的元素重新绘制，并更新缓存，以避免模糊
     * @param order 动画组的顺序
     * @param cacheMap 需要更新缓存的动画元素
     * 当一组动画播完后，如果这组动画中有动画目标元素的scaleX或者scaleY发生变化，
     * 则缓存渲染策略重置为Never，手动渲染一次，然后更新缓存，以避免模糊
     */
    protected updateCache(order: number): void {
        const cacheSet = this.cacheMap.get(order);
        if (!cacheSet) return;
        cacheSet.forEach((animTarget) => {
            // 当前页所有动画结束之前，autoRender为false
            // 设置渲染策略，只会更新各种transform和各种bounds，不会在下一帧触发渲染，需要手动调用
            animTarget.cache.rendering = CacheRendering.Never;
            // 手动渲染一次，渲染过程中会根据CacheRendering的策略更新缓存
            this.renderApplication();

            // 重新开启强制缓存，使用新的缓存，因为后续元素的动画执行过程中，是整个画布重新渲染，
            // 按照Never的策略缓存失效，所以需重新开启强制缓存
            animTarget.cache.rendering = CacheRendering.Always;
        });
    }

    // 处理动画循环时，会触发非当前order的动画更新的bug
    protected handleAnimUpdate(animUpdateParams: IAnimUpdateParams): void {
        const { animModel, order, duration } = animUpdateParams;
        const animModelKey = animModel?.key;
        const animType = animModel?.props.animType;
        // 页面中的动画循环播放时，播放某个order的动画时，会触发相邻order动画的update触发一次,需要阻止
        if (order !== this.currentOrder) {
            return;
        }

        super.handleAnimUpdate(animUpdateParams);
        this.calPerformanceData(animModelKey, animType, duration);
    }

    /**
     * 该order下动画播放完成后，将最后一帧的样式缓存为元素下一order的前翻回退样式
     * 注意：元素的动画order并不一定是连续的，比如元素A在第一次单击页面和第五次单击页面时都会播放动画
     * @param order 播放顺序
     */
    private cacheBackStyle(order: number) {
        if (!this.animTargetKeyOrderMap) {
            this.animTargetKeyOrderMap = getAnimTargetKeyOrderMap(this.slideComponent?.options.slideModel);
        }
        // 1、找到该order下的所有的动画目标元素
        const targetKeyList = this.getSameOrderTargetKeyList(order);
        targetKeyList.forEach((animTargetKey) => {
            // 2、获取动画目标元素的当前样式style
            const animTarget = getTargetComponent(animTargetKey, this.slideComponent);
            if (!animTarget) return;
            const currentStyle = this.getCurrentStyle(animTarget);
            // 3、找到动画目标元素的下一个动画nextOrder
            const keyOrderList = this.animTargetKeyOrderMap.get(animTargetKey);
            if (!keyOrderList?.length) return;
            const [minOrder] = keyOrderList;

            if (order === minOrder) {
                this.handleMinOrder(order, animTargetKey);
            }

            const currentOrderIndex = keyOrderList.indexOf(order);
            const nextOrderIndex = currentOrderIndex + 1;
            const nextOrder = nextOrderIndex < keyOrderList.length ? keyOrderList[nextOrderIndex] : -1;
            this.handleNextOrder(nextOrder, animTargetKey, currentStyle);
        });
    }

    /**
     * 处理该元素第一个动画的前翻回退样式
     * @param order 播放顺序
     * @param animTargetKey 元素key
     */
    private handleMinOrder(order: number, animTargetKey: string): void {
        // 该order为该元素的第一个动画，则该元素的初始状态的样式是第一个动画回退后的样式
        // 第一个动画的order可能值为非负整数
        const keyStyleObj = this.animTargetStyleMap?.get(order);
        const originalStyle = this.originalStyleMap.get(animTargetKey);
        const isEntry = this.checkIsEntry(animTargetKey, order);
        const keyBackStyle = isEntry ? { ...originalStyle, visible: false } : originalStyle;
        if (keyStyleObj) {
            this.animTargetStyleMap?.set(order, { ...keyStyleObj, [animTargetKey]: keyBackStyle });
        } else {
            this.animTargetStyleMap?.set(order, { [animTargetKey]: keyBackStyle });
        }
    }


    /**
     * 处理该元素该order动画播放完成后，nextOrder应回退的样式
     * @param nextOrder 播放顺序
     * @param animTargetKey 元素key
     * @param currentStyle 元素key的canvas component当前的样式
     * 注意： order与nextOrder并不一定是连续的
     */
    private handleNextOrder(nextOrder: number, animTargetKey: string, currentStyle: IAnimSimpleStyle): void {
        // 该order的动画结束帧样式，是nextOrder回退后的样式
        const keyStyleObj = this.animTargetStyleMap?.get(nextOrder);
        if (keyStyleObj) {
            this.animTargetStyleMap?.set(nextOrder, { ...keyStyleObj, [animTargetKey]: currentStyle });
        } else {
            this.animTargetStyleMap?.set(nextOrder, { [animTargetKey]: currentStyle });
        }
    }

    /**
     * 获取该播放顺序下的由所有动画元素的key组成的数组，该数组去重
     * @param order 播放顺序
     * @returns [key1, key2,key3]
     */
    private getSameOrderTargetKeyList(order: number): Array<string> {
        const sameOrderAnimList = this.animList.filter(animModel => animModel?.playOrder === order);
        const targetKeyList = sameOrderAnimList.map(animModel => animModel?.elementModel?.key);
        // 相同order下，目标元素有可能有多个动画，进行去重
        return Array.from(new Set(targetKeyList));
    }

    /**
     * 计算该元素在该order下回退后的样式, 有缓存走缓存，没缓存则实时计算并更新缓存
     * @param order 播放顺序
     * @param targetKey 元素key
     * @returns IAnimSimpleStyle
     */
    private getKeyBackStyle(order: number, targetKey: string): IAnimSimpleStyle {
        const keyBackStyleMap = this.animTargetStyleMap?.get(order) || {};
        let keyBackStyle = keyBackStyleMap[targetKey];
        if (!keyBackStyle) {
            keyBackStyle = this.getOrderBackStyleSync(order, targetKey);
            this.animTargetStyleMap?.set(order, { ...keyBackStyleMap, [targetKey]: keyBackStyle });
        }

        return keyBackStyle;
    }

    /**
     * 实时计算计算该元素在该order下回退后的样式
     * @param order 播放顺序
     * @param targetKey 元素key
     * @returns IAnimSimpleStyle
     */
    private getOrderBackStyleSync(order: number, targetKey: string): IAnimSimpleStyle {
        if (!this.targetAnimMap) {
            this.targetAnimMap = this.getTargetAnimMap(this.animList);
        }
        const animListOfTarget = this.targetAnimMap?.get(targetKey);
        if (!animListOfTarget?.length) return {};

        const orderIndex = this.getOrderLastIndex(order - 1, animListOfTarget);
        const originalStyle = this.originalStyleMap.get(targetKey);
        let keyBackStyle;
        if (this.isFirstAnimOfTarget(order, targetKey)) {
            // 元素的第一个动画的回退样式为该动画的初始态样式，如果第一个动画为入场动画则需隐藏
            const isEntry = this.checkIsEntry(targetKey, order);
            keyBackStyle = isEntry ? { ...originalStyle, visible: false } : originalStyle;
        } else {
            const finishStyleBeforeCurrentOrder = this.getFinishStyleBeforeCurrentOrder(
                animListOfTarget,
                orderIndex,
            );
            // 由于initToStyle中的translateX/Y都是相对于元素自身的，需要修正元素的位置
            keyBackStyle = {
                ...finishStyleBeforeCurrentOrder,
                x: finishStyleBeforeCurrentOrder?.x + originalStyle?.x,
                y: finishStyleBeforeCurrentOrder?.y + originalStyle?.y,
            };
        }

        return keyBackStyle;
    }

    /**
     * 获取该元素经过前index-1个动画播放之后的最后一帧的样式
     * @param animListOfTarget 该元素绑定的所有动画
     * @param orderIndex order在animList中最后出现的位置
     * @returns styleObj 最后一帧的样式
     */
    private getFinishStyleBeforeCurrentOrder(animListOfTarget: IAnimList, orderIndex: number): IAnimSimpleStyle {
        const toStyles = getToStylesOfOrder(animListOfTarget, orderIndex);
        let lastFrameStyle = this.formatStyle(toStyles);
        // 关键帧动画特殊处理
        if (Array.isArray(lastFrameStyle)) {
            lastFrameStyle = lastFrameStyle[lastFrameStyle.length - 1];
        }

        return this.validateStyle(lastFrameStyle);
    }

    /**
     * 获取元素所有的动画中，播放顺序order的动画最后出现的位置
     * @param order 播放顺序，同一元素的同一播放顺序可能会有多个动画
     * @param animListOfTarget  该元素所有动画的播放顺序数组，不去重，有序数组
     * @returns 返回order最后出现的index， 如fn(1,[1,1,1,3,6]), 返回2
     */
    private getOrderLastIndex(order: number, animListOfTarget: IAnimList): number {
        const index = animListOfTarget.findIndex(animModel => animModel?.order > order);
        return index === -1 ? animListOfTarget.length - 1 : index - 1;
    }

    /**
     * 检查是否为该元素的第一个动画
     * @param order 播放顺序
     * @param targetKey 元素key
     * @returns boolean
     */
    private isFirstAnimOfTarget(order: number, targetKey: string): boolean {
        if (!this.animTargetKeyOrderMap) {
            this.animTargetKeyOrderMap = getAnimTargetKeyOrderMap(this.slideComponent?.options.slideModel);
        }
        const orderListOfSameTarget = this.animTargetKeyOrderMap?.get(targetKey);
        if (order === orderListOfSameTarget?.[0]) return true;
        return false;
    }

    /**
     * 初始化animTargetStyleMap，并缓存至slideModel
     */
    private initAnimTargetStyleMap(): void | undefined {
        if (!this.slideComponent) return;
        // @ts-ignore
        this.animTargetStyleMap = this.slideComponent.options.slideModel?.animTargetStyleMap || new Map();
        if (!this.animTargetStyleMap?.size) {
            // @ts-ignore
            this.slideComponent.options.slideModel.animTargetStyleMap = this.animTargetStyleMap;
        }
    }

    /**
     * 补齐默认属性
     * @param style 样式对象
     */
    private validateStyle(style): IAnimSimpleStyle {
        const tempStyle = style;
        Object.keys(DEFAULT_CANVAS_STYLE).forEach((attrName) => {
            if (!this.hasValidProperty(tempStyle, attrName)) {
                tempStyle[attrName] = DEFAULT_CANVAS_STYLE[attrName];
            }
        });
        return tempStyle;
    }

    /**
     * 判断元素第一个播放顺序为order的动画是否为入场动画
     * @param targetKey 元素key
     * @param order 动画播放顺序
     * @returns boolean
     */
    private checkIsEntry(targetKey: string, order: number): boolean {
        const [animModel] = this.animList.filter(animModel => animModel?.elementModel?.key === targetKey);
        const presetClass = animModel?.props?.presetClass;
        return presetClass === presetClassTypes.ENTR && animModel?.playOrder === order;
    }

    /**
     * 上报第一组动画的响应时间
     * @param order 动画顺序
     */
    private tdwAnimReactTime(order: number): void | undefined {
        const animOrder = `${this.slideComponent?.options.slideModel.key}-${order}`;
        const endObj: IRecordData | undefined = TimeRecord.end(animOrder);
        if (!endObj) return;
        tdw({
            opername: TDWOperationType.PPT_QUALITY,
            module: TDWModuleType.PC_PLAY,
            action: TDWActionType.ANIM_REACT,
            ver6: JSON.stringify({ duration: Math.round(endObj.duration), order: animOrder }),
        });
    }


    /**
     * 使用动画缓存
     * @param animTarget 动画目标元素
     */
    private useAnimCache(animTarget: IAnimTarget, animType: string): void {
        if (shouldDisableAnimCacheAndFilter(animTarget)) {
            return;
        }
        // 开启使用使用缓存渲染策略
        if (animTarget.cache.rendering !== CacheRendering.Always) {
            animTarget.cache.rendering = CacheRendering.Always;
        }
        // 目标元素已经有缓存，则在动画开始时不必重新生成缓存
        if (animTarget.cache.renderBuffer) return;

        // 有些入场动画（比如玩具风车），初始状态的scale均为0，在进行缓存前，需要将scale置为1，以得到正确的缓存
        if (!animTarget.scaleX) {
            animTarget.scaleX = 1;
        }
        if (!animTarget.scaleY) {
            animTarget.scaleY = 1;
        }

        if (animType === AnimType.EntrBoomerang) {
            animTarget.angle = 0;
        }

        this.renderApplication();

        // 生成缓存
        animTarget.makeCache(false, false);
    }

    /**
     * 更新缓存Map
     * @param toStyle 单个动画结束时的样式
     * @param order 单个动画所属组的顺序
     * @param animTarget 动画目标元素
     * @param cacheMap order => Set<animTarget>
     * Map中存储的是一组动画结束时需要更新缓存的Map
     */
    private updateCacheMap(
        fromStyle,
        toStyle,
        order: number,
        animTarget: IAnimTarget,
    ): void {
        if (toStyle.scaleX !== fromStyle.scaleX || toStyle.scaleY !== fromStyle.scaleY) {
            let cacheSet = this.cacheMap.get(order);
            if (!cacheSet) {
                cacheSet = new Set();
                this.cacheMap.set(order, cacheSet);
            }
            cacheSet.add(animTarget);
        }
    }

    /**
     * 判断动画目标元素之上是否有可点击元素
     * @param animTargetKey 动画目标元素
     * @param slideModel 当前页的slideModel
     * @returns boolean
     */
    private isClickOverAnim(animTargetKey, slideModel): boolean {
        const slideChildren = slideModel.children;
        const index = slideChildren.findIndex(childModel => childModel.key === animTargetKey);
        if (index === -1 || index === slideChildren.length - 1) return false;
        for (let i = index; i < slideChildren.length; i++) {
            const control = this.slideComponent?.options.getPlayerReduxStore?.().getState().control;
            if (checkHasClickEle(slideChildren[i], control)) return true;
        }
        return false;
    }

    /**
     * 存储当前页是否有可点击元素
     */
    private setSlideClickMap(): void {
        if (!AllowLayer) return;
        const slideModel = this.slideComponent?.options.slideModel;
        if (!slideModel || this.layerService.slideHasClickEleMap.has(slideModel.key)) return;
        const control = this.slideComponent?.options.getPlayerReduxStore?.()?.getState()?.control;
        if (!control) return;
        this.layerService.slideHasClickEleMap.set(slideModel.key, checkHasClickEle(slideModel, control));
    }

    /**
     * 判断是否能进行分层
     * @param animTargetKey 动画目标元素
     * @param slideModel 当前页slideModel
     * @returns boolean
     */
    private canBeSliced(animTargetKey, slideModel): boolean {
        if (!AllowLayer) return false;
        // 当前页没有可点击元素，则可以进行分层处理
        if (!this.layerService.slideHasClickEleMap.get(slideModel.key)) return true;
        // 动画元素之上没有可点击元素，并且baseLayer没有动画运行, 则可以进行分层处理
        // 其余情况不进行分层处理
        return !this.isClickOverAnim(animTargetKey, slideModel) && !this.layerService.hasActiveAnimInBaseLayer;
    }

    // 上报动画fps和内存占用
    private tdwAnimData() {
        const fpsStr = this.concatStr(this.fpsArr);
        const memoryStr = this.concatStr(this.memoryArr);
        tdw({
            opername: TDWOperationType.PPT_QUALITY,
            module: TDWModuleType.PC_PLAY,
            action: TDWActionType.ANIM_PERFORMANCE,
            ver6: fpsStr, // 示例：'60#60#55#59'
            ver7: memoryStr, // 示例：'200#300#600'
            ver9: this.slideComponent?.options.slideModel?.elementNumInfo.totel, // 当前页元素总量
            ver10: JSON.stringify(this.fpsArr), // fps
            ver11: JSON.stringify(this.memoryArr), // memory
        });
    }

    // 初始化用于上报的动画数据
    private initTdwData(animModelKey: string, animType: string): void {
        const key = `${animModelKey}//${animType}`;
        this.animPrevTimeMap[key] = 0;
        this.animFrameCountMap[key] = 0;
        this.animFPSMap[key] = [];
        this.animMemoryMap[key] = [];
    }

    // 计算并存储动画运行过程中的帧率数据、内存占用数据
    private calPerformanceData(animModelKey: string, animType: string, duration: number): void {
        if (!window?.performance?.now) return;
        const key = `${animModelKey}//${animType}`;
        const prevTime = this.animPrevTimeMap[key];
        // animStart之前的animUpdate需要阻止
        if (typeof prevTime === 'undefined') return;
        this.animFrameCountMap[key] += 1;
        // animStart之后的第一次animUpdate, 不必上报fps，只需更新时间标识
        const now = performance.now();
        if (prevTime === 0) {
            this.animPrevTimeMap[key] = now;
            return;
        }
        const deltaTime = now - prevTime;
        if (deltaTime < this.getSampleInterval(duration)) return;
        this.calFPS(animModelKey, animType, deltaTime, now);
        this.calMemory(animModelKey, animType);
    }

    /**
     * 获取采样间隔
     * @param duration 动画持续时间，单位s
     * @returns 采样间隔，单位ms
     */
    private getSampleInterval(duration: number): number {
        const ONE_SECOND = 1;
        // 持续时间1s以下的动画，采样间隔为400ms
        if (duration < ONE_SECOND) return SAMPLE_INTERVAL.FREQUENT;
        // 持续时间1s的动画，采样间隔为950ms
        if (duration === ONE_SECOND) return SAMPLE_INTERVAL.MEDIUM;
        // 持续时间1s以上的动画，采样间隔为1s
        return SAMPLE_INTERVAL.COMMON;
    }

    // 计算并存储动画运行过程中的帧率
    private calFPS(animModelKey: string, animType: string, deltaTime: number, now: number) {
        const key = `${animModelKey}//${animType}`;
        const fps = Math.round((this.animFrameCountMap[key] / deltaTime) * 1000);
        this.animFPSMap[key].push(fps);
        this.animFrameCountMap[key] = 0;
        this.animPrevTimeMap[key] = now;
        this.fpsArr.push({ value: fps, type: animType });
    }

    // 计算并存储动画运行过程中内存占用（当前使用的js堆），单位为MB
    private calMemory(animModelKey: string, animType: string) {
        const key = `${animModelKey}//${animType}`;
        const usedJSHeapSize = getMemory();
        if (!usedJSHeapSize) return;
        this.animMemoryMap[key]?.push(usedJSHeapSize);
        this.memoryArr.push({ value: usedJSHeapSize, type: animType });
    }

    // 每次动画结束时，重置时间标识
    private resetAnimPrevTime(animModelKey: string, animType: string) {
        const key = `${animModelKey}//${animType}`;
        this.animPrevTimeMap[key] = 0;
        this.animFrameCountMap[key] = 0;
    }

    // 拼接字符串，用于上报
    private concatStr(arr: Array<{ [key: string]: string | number }>) {
        return arr.reduce((prevValue, { value }, index) => {
            if (!index) {
                return `${value}`;
            }

            return `${prevValue}#${value}`;
        }, '');
    }

    /**
     * 处理跨页上翻时颜色动画最后一帧样式
     * @param styleObj IAnimSimpleStyle
     * @param animTarget IAnimTarget
     */
    private handleColorAnimStyle(styleObj: IAnimSimpleStyle, animTarget: IAnimTarget, animType: AnimType): void {
        if (!colorAnimTypeList.includes(animType)) return;
        if (!styleObj.fillColor && animTarget.fillColor) {
            styleObj.fillColor = COLOR.white;
        }
        if (!styleObj.fillColor && !styleObj.lineColor && !styleObj.fontColor) return;
        const needUpdateFontList = [AnimCanvasProperty.FONT_COLOR, AnimCanvasProperty.FONT_SIZE];
        const { factoryOptions } = animTarget;
        ATTR_LIST.forEach((colorName) => {
            if (!styleObj[colorName]) return;
            factoryOptions.anim = {
                ...animTarget.factoryOptions.anim,
                [colorName]: styleObj[colorName],
                ...(needUpdateFontList.includes(colorName) ? { needUpdateFont: true } : {}),
            };
        });
        animTarget.factoryUpdateComponent();
    }
}
